import { provideHttpClient, withInterceptors } from "@angular/common/http";
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from "@angular/core";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideRouter, Router, withInMemoryScrolling } from "@angular/router";
import { provideTransloco } from "@jsverse/transloco";
import { provideFormlyCore } from "@ngx-formly/core";
import * as Sentry from "@sentry/angular";
import { MessageService } from "primeng/api";
import { providePrimeNG } from "primeng/config";
import { DialogService, DynamicDialogModule } from "primeng/dynamicdialog";
import { ApiConfiguration } from "./apiClient/api-configuration";
import { routes } from "./app.routes";
import { langCodes } from "./domain/languages";
import { AppUrlsService } from "./services/app-urls.service";
import { TranslocoHttpLoader } from "./services/transloco-loader";
import { BrandConfig } from "./shared/branding/brand-config";
import { provideBranding } from "./shared/branding/branding.providers";
import { apiInterceptor } from "./shared/interceptors/api-interceptor";
import { CalendarComponent } from "./shared/ui/components/formly-types/calendar/calendar.type";
import { CheckboxComponent } from "./shared/ui/components/formly-types/checkbox/checkbox.type";
import { FileUploadComponent } from "./shared/ui/components/formly-types/file/file.type";
import { FileDownloadDisplayComponent } from "./shared/ui/components/formly-types/file-download-display/file-download-display.type";
import { HeaderComponent } from "./shared/ui/components/formly-types/header/header.type";
import { JsonInputComponent } from "./shared/ui/components/formly-types/json/json.type";
import { MonthYearComponent } from "./shared/ui/components/formly-types/month-year/month-year.type";
import { NoteComponent } from "./shared/ui/components/formly-types/note/note.type";
import { RadioComponent } from "./shared/ui/components/formly-types/radio/radio.type";
import { RepeatTypeComponent } from "./shared/ui/components/formly-types/repeat-section/repeat-section.type";
import { SelectOrgUsersComponent } from "./shared/ui/components/formly-types/select/select-org-user.type";
import { SelectComponent } from "./shared/ui/components/formly-types/select/select.type";
import { TextInputComponent } from "./shared/ui/components/formly-types/text/text.type";
import { TextDisplayComponent } from "./shared/ui/components/formly-types/text-display/text-display.type";
import { TextTranslatableComponent } from "./shared/ui/components/formly-types/text-translatable/text-translatable.type";
import { TreeCheckboxComponent } from "./shared/ui/components/formly-types/tree-checkbox/tree-checkbox.type";
import { TextEditorComponent } from "./shared/ui/components/formly-types/wysiwyg/wysiwyg.type";
import { WysiwygDisplayComponent } from "./shared/ui/components/formly-types/wysiwyg-display/wysiwyg-display.type";
import { bicValidator } from "./shared/ui/components/formly-validators/bic.validator";
import { emailValidator } from "./shared/ui/components/formly-validators/email.validator";
import { ibanValidator } from "./shared/ui/components/formly-validators/iban.validator";
import { int32Validator } from "./shared/ui/components/formly-validators/int32.validator";
import { numberValidator } from "./shared/ui/components/formly-validators/number.validator";
import { phoneNumberValidator } from "./shared/ui/components/formly-validators/phone-number.validator";
import { finnishSsnValidator } from "./shared/ui/components/formly-validators/ssn-finnish.validator";
import { ssnValidator } from "./shared/ui/components/formly-validators/ssn.validator";

export function createAppConfig(brand: BrandConfig): ApplicationConfig {
  return {
    providers: [
      provideBranding(brand),
      provideZoneChangeDetection({ eventCoalescing: true }),
      // Enable scrolling to top on route change for better UX
      provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: "enabled" })),
      provideHttpClient(withInterceptors([apiInterceptor])),
      // Set up generated API clients to get their base URL from the AppsUrlService
      {
        provide: ApiConfiguration,
        useFactory: (appUrls: AppUrlsService) => {
          const config = new ApiConfiguration();
          config.rootUrl = appUrls.apiUrl;
          return config;
        },
        deps: [AppUrlsService],
      },
      provideAnimationsAsync(),
      providePrimeNG({
        theme: {
          preset: brand.primeNgPreset,
          options: {
            darkModeSelector: ".app-dark",
            cssLayer: {
              name: "primeng",
              order: "theme, base, primeng",
            },
          },
        },
      }),
      {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler(),
      },
      {
        provide: Sentry.TraceService,
        deps: [Router],
      },
      provideAppInitializer(() => {
        inject(Sentry.TraceService);
      }),
      provideTransloco({
        config: {
          availableLangs: [...langCodes],
          defaultLang: "fi",
          reRenderOnLangChange: true,
          prodMode: !isDevMode(),
        },
        loader: TranslocoHttpLoader,
      }),
      MessageService,
      provideFormlyCore([
        {
          types: [
            { name: "repeat", component: RepeatTypeComponent },
            { name: "textTranslatable", component: TextTranslatableComponent },
            { name: "wysiwyg", component: TextEditorComponent },
            { name: "json", component: JsonInputComponent },
            { name: "checkbox", component: CheckboxComponent },
            { name: "text", component: TextInputComponent },
            { name: "calendar", component: CalendarComponent },
            { name: "monthYear", component: MonthYearComponent },
            { name: "select", component: SelectComponent },
            { name: "header", component: HeaderComponent },
            { name: "note", component: NoteComponent },
            { name: "radio", component: RadioComponent },
            { name: "file", component: FileUploadComponent },
            { name: "fileDownloadDisplay", component: FileDownloadDisplayComponent },
            { name: "textDisplay", component: TextDisplayComponent },
            { name: "wysiwygDisplay", component: WysiwygDisplayComponent },
            { name: "treeCheckbox", component: TreeCheckboxComponent },
            { name: "selectOrgUser", component: SelectOrgUsersComponent },
            // When adding a new type here, remember to also add it to validFormlyTypes in processDefinition.ts in the database package!
          ],
          validators: [
            { name: "email", validation: emailValidator },
            { name: "number", validation: numberValidator },
            { name: "int32", validation: int32Validator },
            { name: "phoneNumber", validation: phoneNumberValidator },
            { name: "finnishSsn", validation: finnishSsnValidator }, // TODO: Delete. Legacy, kept for forms already in production. New forms use "ssn" with { countries: ["fi"] }.
            { name: "ssn", validation: ssnValidator },
            { name: "iban", validation: ibanValidator },
            { name: "bic", validation: bicValidator },
          ],
          validationMessages: [
            /**
             * t(app.formly-types.validationMessages.fieldRequired)
             * t(app.formly-types.validationMessages.invalidEmail)
             * t(app.formly-types.validationMessages.invalidNumber)
             * t(app.formly-types.validationMessages.invalidInteger)
             * t(app.formly-types.validationMessages.invalidPhoneNumber)
             * t(app.formly-types.validationMessages.invalidSsn)
             * t(app.formly-types.validationMessages.invalidIban)
             * t(app.formly-types.validationMessages.invalidBic)
             */
            { name: "required", message: "app.formly-types.validationMessages.fieldRequired" },
            { name: "email", message: "app.formly-types.validationMessages.invalidEmail" },
            { name: "number", message: "app.formly-types.validationMessages.invalidNumber" },
            { name: "int32", message: "app.formly-types.validationMessages.invalidInteger" },
            { name: "phoneNumber", message: "app.formly-types.validationMessages.invalidPhoneNumber" },
            { name: "finnishSsn", message: "app.formly-types.validationMessages.invalidSsn" }, // TODO: Delete. Legacy, kept for forms already in production. New forms use "ssn" with { countries: ["fi"] }.
            { name: "ssn", message: "app.formly-types.validationMessages.invalidSsn" },
            { name: "iban", message: "app.formly-types.validationMessages.invalidIban" },
            { name: "bic", message: "app.formly-types.validationMessages.invalidBic" },
          ],
        },
      ]),
      // Make DialogService available application-wide
      importProvidersFrom(DynamicDialogModule),
      DialogService,
    ],
  };
}
